import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usersApi } from '../../services/api';
import { User } from '../../types';
import toast from 'react-hot-toast';
import {
  User as UserIcon,
  Plus,
  Edit,
  Trash2,
  Crown,
  Shield,
  UserCheck,
  Mail,
  Calendar,
  Search
} from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';

const UsersPage: React.FC = () => {
  usePageTitle('Usuários');
  const { canAccess, isOwner, currentCompany } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'member' as 'owner' | 'admin' | 'member'
  });

  useEffect(() => {
    if (canAccess('manage_users')) {
      loadUsers();
    }
  }, [canAccess]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await usersApi.getAll();
      setUsers(response.users);
    } catch (error: any) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    try {
      await usersApi.create(formData);
      toast.success('Usuário criado com sucesso!');
      setShowCreateModal(false);
      setFormData({ name: '', email: '', password: '', role: 'member' });
      loadUsers();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao criar usuário');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingUser || !formData.name) {
      return;
    }

    try {
      await usersApi.update(editingUser.id, {
        name: formData.name,
        role: formData.role
      });
      toast.success('Usuário atualizado com sucesso!');
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'member' });
      loadUsers();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao atualizar usuário');
    }
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }

    try {
      await usersApi.delete(userId);
      toast.success('Usuário excluído com sucesso!');
      loadUsers();
    } catch (error: any) {
      toast.error(error.error || 'Erro ao excluir usuário');
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'member' });
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="h-4 w-4 text-mostarda-dark" />;
      case 'admin': return <Shield className="h-4 w-4 text-bordo" />;
      default: return <UserCheck className="h-4 w-4 text-musgo" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Proprietário';
      case 'admin': return 'Administrador';
      default: return 'Usuário';
    }
  };

  if (!canAccess('manage_users')) {
    return (
      <div className="bg-white rounded-xl border border-tinta/10 shadow-sm py-12 text-center">
        <UserIcon className="mx-auto h-12 w-12 text-tinta-soft/60" />
        <h3 className="mt-2 font-display font-bold text-tinta">Acesso negado</h3>
        <p className="mt-1 text-sm text-tinta-soft italic">
          Você não tem permissão para gerenciar usuários.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bordo"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-tinta leading-tight">
            Seus <span className="italic text-bordo">usuários</span>
          </h1>
          <p className="mt-2 text-sm text-tinta-soft">
            Gerencie os usuários da empresa {currentCompany?.name}
          </p>
        </div>
        {isOwner && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 sm:mt-0 inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Usuário
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-tinta-soft" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-2.5 bg-white border border-tinta/15 rounded-lg text-sm text-tinta placeholder:italic placeholder:text-tinta-soft/70 focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors"
          placeholder="Buscar usuários..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Users List */}
      <div className="bg-white border border-tinta/10 shadow-sm overflow-hidden rounded-xl">
        <ul className="divide-y divide-tinta/8">
          {filteredUsers.map((user) => (
            <li key={user.id}>
              <div className="px-4 py-4 flex items-center justify-between hover:bg-creme-light/50 transition-colors">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-bordo/10 border border-bordo/20 flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-bordo" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <p className="text-sm font-bold text-tinta">{user.name}</p>
                      <div className="ml-2 flex items-center">
                        {getRoleIcon(user.role)}
                        <span className="text-xs text-tinta-soft font-semibold uppercase tracking-wider ml-1">
                          {getRoleLabel(user.role)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-tinta-soft">
                      <Mail className="h-4 w-4 mr-1" />
                      {user.email}
                    </div>
                    <div className="flex items-center text-xs text-tinta-soft/70 italic mt-1">
                      <Calendar className="h-3 w-3 mr-1" />
                      Criado em {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      {user.last_login_at && (
                        <span className="ml-4">
                          Último login: {new Date(user.last_login_at).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {isOwner && user.role !== 'owner' && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(user)}
                      className="text-tinta-soft hover:text-bordo hover:bg-bordo/8 rounded-md p-1.5 transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-tinta-soft hover:text-bordo hover:bg-bordo/8 rounded-md p-1.5 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        {filteredUsers.length === 0 && (
          <div className="bg-creme-light py-12 text-center">
            <UserIcon className="mx-auto h-12 w-12 text-tinta-soft/60" />
            <h3 className="mt-2 font-display font-bold text-tinta">Nenhum usuário encontrado</h3>
            <p className="mt-1 text-sm text-tinta-soft italic">
              {searchTerm ? 'Tente ajustar sua busca.' : 'Comece criando um novo usuário.'}
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingUser) && (
        <div className="fixed inset-0 bg-tinta/60 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 w-96 bg-white rounded-xl border border-tinta/10 shadow-xl">
            <div>
              <h3 className="font-display text-xl font-bold text-tinta mb-4">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>

              <form onSubmit={editingUser ? handleUpdate : handleCreate} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">Nome</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors disabled:bg-creme-light disabled:opacity-70"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">Email</label>
                  <input
                    type="email"
                    required={!editingUser}
                    disabled={!!editingUser}
                    className="mt-1 block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors disabled:bg-creme-light disabled:opacity-70"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                {!editingUser && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">Senha</label>
                    <input
                      type="password"
                      required
                      className="mt-1 block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors disabled:bg-creme-light disabled:opacity-70"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">Função</label>
                  <select
                    className="mt-1 block w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors disabled:bg-creme-light disabled:opacity-70"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                  >
                    <option value="member">Usuário</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-creme-light hover:text-tinta transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors"
                  >
                    {editingUser ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
